import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { requestAPI } from '../../data/api'
import { getModel } from '../../data/Model'
import DigitalPropertyContactEnrichmentCard from './DigitalPropertyContactEnrichmentCard'
import './ContactEnrichment.css'

class ContactEnrichment extends Component {
	constructor(props) {
		super(props)
		this.state = {
			violations: [],
			loading: true,
			availableClients: [],
			selectedClientId: '',
			loadingClients: true,
			onlyNordics: false,
			error: null,
		}
	}

	// requestAPI resolves with the raw Response when the API replies with a
	// non-JSON body, which is what sendStatus(500) produces. Without this the
	// failure only surfaces later as "organizations.sort is not a function".
	assertArray = (response, message) => {
		if (!Array.isArray(response)) {
			throw new Error(message)
		}
		return response
	}

	async componentDidMount() {
		await this.loadAvailableClients()
	}

	loadAvailableClients = async () => {
		this.setState({ loadingClients: true, error: null })
		try {
			// Get clients with contact_enrichment violations available
			const { onlyNordics } = this.state
			const Model = getModel()
			const userId = Model.me.data.user_id
			const response = await requestAPI(
				`/violations/statecount/contact_enrichment?nordics=${onlyNordics}&userId=${userId}`,
				'GET',
			)

			// Process the response - expecting an array of objects
			const availableClients = this.assertArray(
				response,
				'Failed to load the client list.',
			).map((item) => {
				const total = Number(item.violations) || 0
				const locked = Number(item.locked_count) || 0
				// Work is handed out by digital property, so a violation without one can never
				// be claimed here however long it waits. Subtracted from what we advertise as
				// available — otherwise a partner reads as having 20 waiting and selecting it
				// shows an empty screen — but still reported, so the backlog stays visible.
				const unresolved = Number(item.unresolved_count) || 0
				return {
					client_id: item.client_id,
					client_name: item.name || `Client ${item.client_id}`,
					violation_count: total,
					locked_count: locked,
					unresolved_count: unresolved,
					available_count: Math.max(total - locked - unresolved, 0),
				}
			})

			// Do not auto-select any client by default
			this.setState({
				availableClients,
				selectedClientId: '',
				loadingClients: false,
				loading: false,
			})
		} catch (error) {
			console.error('Failed to load available clients:', error)
			this.setState({
				availableClients: [],
				loadingClients: false,
				loading: false,
				error: error.message || 'Failed to load the client list.',
			})
		}
	}

	loadOrganizations = async (clientId) => {
		// Don't make API call if no client is selected
		if (!clientId || clientId === '') {
			this.setState({
				organizations: [],
				loading: false,
				error: null,
			})
			return
		}

		this.setState({ loading: true, error: null })
		try {
			const Model = getModel()
			const userId = Model.me.data.user_id
			const { onlyNordics } = this.state
			const url = `/organizations/contact-enrichment?nordics=${onlyNordics}&userId=${userId}&clientId=${clientId}`

			// Load organizations ready for contact enrichment with contact information included
			const organizations = this.assertArray(
				await requestAPI(url, 'GET'),
				'Failed to load violations for this client.',
			)

			organizations.sort(
				(a, b) =>
					(a.violations ? a.violations.length : 0) -
					(b.violations ? b.violations.length : 0),
			)

			this.setState({
				organizations,
				loading: false,
			})
		} catch (error) {
			console.error('Failed to load organizations:', error)
			this.setState({
				organizations: [],
				loading: false,
				error: error.message || 'Failed to load violations for this client.',
			})
		}
	}

	handleClientChange = async (e) => {
		const selectedClientId = e.target.value
		// Update selected client
		this.setState({ selectedClientId })
		// Load organizations and join new client
		await this.loadOrganizations(selectedClientId)
	}

	handleNordicToggle = () => {
		this.setState(
			(prevState) => ({ onlyNordics: !prevState.onlyNordics }),
			async () => {
				await this.loadAvailableClients()
				if (this.state.selectedClientId) {
					await this.loadOrganizations(this.state.selectedClientId)
				}
			},
		)
	}

	// Cleanup on component unmount
	componentWillUnmount() {
		// Nothing to clean up
	}

	refresh = async () => {
		await this.refreshOrganizations()
	}

	handleOrganizationChange = (index, updatedOrganization) => {
		this.setState((prevState) => ({
			organizations: prevState.organizations.map((organization, i) =>
				i === index ? updatedOrganization : organization,
			),
		}))
	}

	refreshOrganizations = async () => {
		const { selectedClientId, onlyNordics } = this.state
		if (!selectedClientId && !onlyNordics) return

		// Find which violation is currently in view
		const violationElements = document.querySelectorAll('.violation-card')
		let targetViolationId = null

		// Find the first violation card that's at least 50% visible in viewport
		for (let el of violationElements) {
			const rect = el.getBoundingClientRect()
			const windowHeight = window.innerHeight
			if (rect.top < windowHeight * 0.5 && rect.bottom > windowHeight * 0.25) {
				const violationId = el.getAttribute('data-violation-id')
				if (violationId) {
					targetViolationId = parseInt(violationId)
					break
				}
			}
		}

		try {
			// Reload organizations from API with proper nordics filter
			const Model = getModel()
			const userId = Model.me.data.user_id
			const url = `/organizations/contact-enrichment?nordics=${onlyNordics}&userId=${userId}&clientId=${selectedClientId}`

			const updatedOrganizations = this.assertArray(
				await requestAPI(url, 'GET'),
				'Failed to refresh violations for this client.',
			)

			updatedOrganizations.sort(
				(a, b) =>
					(a.violations ? a.violations.length : 0) -
					(b.violations ? b.violations.length : 0),
			)

			this.setState(
				{
					organizations: updatedOrganizations,
					error: null,
				},
				() => {
					// After state update, scroll to the same violation if it still exists
					if (targetViolationId) {
						const targetElement = document.querySelector(
							`[data-violation-id="${targetViolationId}"]`,
						)
						if (targetElement) {
							targetElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							})
						}
					}
				},
			)
		} catch (error) {
			console.error('Failed to refresh organizations:', error)
			this.setState({
				error: error.message || 'Failed to refresh violations for this client.',
			})
		}
	}

	render() {
		const {
			organizations,
			loading,
			availableClients,
			selectedClientId,
			loadingClients,
			error,
		} = this.state

		return (
			<div className='contact-enrichment-container'>
				<div className='contact-enrichment-header'>
					<h1 className='contact-enrichment-title'>Contact Enrichment</h1>
					<p className='contact-enrichment-subtitle'>
						Assign contact information to violations ready for contact
						enrichment
					</p>
					<Link
						to='/contact-actions'
						className='contact-actions-history-button'
					>
						Contact Actions History
					</Link>
				</div>

				{/* Client Selection Dropdown */}
				<div
					className='client-selection'
					style={{
						marginBottom: '24px',
						padding: '16px',
						backgroundColor: '#f8f9fa',
						borderRadius: '8px',
					}}
				>
					<label
						style={{
							display: 'block',
							fontWeight: '600',
							marginBottom: '8px',
							color: '#374151',
						}}
					>
						Select Client
					</label>
					{loadingClients ? (
						<div style={{ padding: '8px' }}>Loading clients...</div>
					) : availableClients.length === 0 ? (
						<div style={{ padding: '8px', color: '#6b7280' }}>
							No clients with contact enrichment violations available
						</div>
					) : (
						<select
							value={selectedClientId}
							onChange={this.handleClientChange}
							style={{
								width: '100%',
								padding: '10px 12px',
								fontSize: '14px',
								border: '1px solid #d1d5db',
								borderRadius: '6px',
								backgroundColor: 'white',
								cursor: 'pointer',
								outline: 'none',
							}}
						>
							<option value=''>-- Select a client --</option>
							{availableClients.map((client) => (
								<option key={client.client_id} value={client.client_id}>
									{client.client_name} ({client.violation_count} total,{' '}
									{client.locked_count} locked,{' '}
									{client.unresolved_count > 0
										? `${client.unresolved_count} without a digital property, `
										: ''}
									{client.available_count} available to you)
								</option>
							))}
						</select>
					)}
				</div>

				{/* Nordic Countries Toggle */}
				<div
					style={{
						padding: '16px',
						marginBottom: '20px',
						backgroundColor: '#f8f9fa',
						borderRadius: '8px',
						display: 'flex',
						alignItems: 'center',
						gap: '12px',
					}}
				>
					<label
						style={{
							display: 'flex',
							alignItems: 'center',
							cursor: 'pointer',
							userSelect: 'none',
						}}
					>
						<input
							type='checkbox'
							checked={this.state.onlyNordics}
							onChange={this.handleNordicToggle}
							style={{
								width: '18px',
								height: '18px',
								cursor: 'pointer',
								marginRight: '8px',
							}}
						/>
						<span
							style={{
								fontWeight: '600',
								color: '#374151',
								fontSize: '14px',
							}}
						>
							Only Nordic Countries
						</span>
					</label>
					<span
						style={{
							fontSize: '12px',
							color: '#6b7280',
							marginLeft: 'auto',
						}}
					>
						{this.state.onlyNordics
							? 'Showing only Nordic countries'
							: 'Excluding Nordic countries'}
					</span>
				</div>

				{loading ? (
					<div className='loading-container'>
						<div className='loading-spinner'></div>
						<p className='loading-text'>Loading violations...</p>
					</div>
				) : error ? (
					<div className='error-container'>
						<div className='error-icon'>⚠️</div>
						<p className='error-text'>{error}</p>
						<p className='error-hint'>
							This is a problem loading the data, not an empty result — there may
							still be violations waiting. Try again, and report it if it keeps
							happening.
						</p>
						<button
							className='error-retry'
							onClick={() =>
								selectedClientId
									? this.loadOrganizations(selectedClientId)
									: this.loadAvailableClients()
							}
						>
							Try again
						</button>
					</div>
				) : !selectedClientId ? (
					<div className='empty-container'>
						<div className='empty-icon'>📋</div>
						<p className='empty-text'>Please select a client</p>
					</div>
				) : organizations.length === 0 ? (
					<div className='empty-container'>
						<div className='empty-icon'>📋</div>
						<p className='empty-text'>
							No violations ready for contact enrichment
						</p>
					</div>
				) : (
					<div>
						<div className='summary-bar'>
							<div>
								<span className='summary-text'>
									{organizations.length} organization
									{organizations.length !== 1 ? 's' : ''} ready for contact
									assignment
								</span>
							</div>
						</div>

						<div className='violations-grid'>
							{organizations.map((organization, index) => (
								<DigitalPropertyContactEnrichmentCard
									key={organization.organizationId || index}
									organization={organization}
									index={index}
									onSave={this.refresh}
									onSubmit={this.refresh}
									onOrganizationChange={(updatedOrganization) =>
										this.handleOrganizationChange(index, updatedOrganization)
									}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		)
	}
}

ContactEnrichment.contextTypes = {
	gettext: PropTypes.func.isRequired,
}

export default ContactEnrichment
